/** @jsxImportSource @emotion/react */
import { CircularProgress, Typography } from '@mui/material'
import { useFetchPropertyInformation } from '@app/components/Property/hooks/useFetchPropertyInformation'
import { mapPropertyInformationResponse } from '@app/components/Property/types'
import { CenteredBox } from '@app/components/ui/CenteredBox/CenteredBox'
import { propertyConfigAtom, selectedAssetInfoAtom } from '@app/state/propertyAtoms'
import { useAtom, useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import PropertyDetails from '../../PropertyDetails'

export const DetailsTab = () => {
  const { id } = useParams<{ id: string }>()
  const {
    data: propertyConfiguration,
    isSuccess: propertyConfigIsReady,
    isLoading: propertyConfigLoading,
    error: propertyConfigError,
  } = useAtomValue(propertyConfigAtom)

  const assetType = propertyConfiguration?.asset_type

  const shouldFetchProperty = Boolean(id && propertyConfigIsReady && assetType)

  const {
    data: property,
    isLoading: propertyLoading,
    error: propertyError,
  } = useFetchPropertyInformation(assetType ?? '', id ?? '')

  const [{ data: propertyInfoResponse, isLoading: atomLoading }] = useAtom(selectedAssetInfoAtom)

  const propertyInfo = useMemo(
    () => (propertyInfoResponse ? mapPropertyInformationResponse(propertyInfoResponse) : null),
    [propertyInfoResponse],
  )

  const loading = propertyConfigLoading || (shouldFetchProperty && propertyLoading) || atomLoading
  const error = propertyConfigError || propertyError

  if (!id) return <Navigate to="/property" replace />

  if (loading) {
    return (
      <CenteredBox>
        <CircularProgress color="info" />
      </CenteredBox>
    )
  }

  if (error) {
    return (
      <CenteredBox>
        <Typography color="error">Error loading property details.</Typography>
      </CenteredBox>
    )
  }

  const data = property ?? propertyInfo

  return <PropertyDetails data={data ?? null} loading={loading} />
}
